import React, { useState, useEffect } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import { toast } from "sonner";
import { useTranslation, Trans } from "react-i18next";
import { ArrowRight, Building2, ShieldCheck, Check, Sparkles } from "lucide-react";
import api, { apiError } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { roleHome, INDUSTRIES, COMPANY_SIZES } from "@/lib/constants";
import { Logo } from "@/components/Logo";
import { LanguageSwitcher } from "@/components/LanguageSwitcher";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Checkbox } from "@/components/ui/checkbox";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Spinner } from "@/components/ui-bits";

export function AuthLayout({ children, title, subtitle }) {
  const { t } = useTranslation();
  return (
    <div className="min-h-screen grid lg:grid-cols-[1fr_1.1fr]">
      {/* Brand panel */}
      <div className="hidden lg:flex flex-col justify-between bg-sidebar border-r border-border p-10 relative overflow-hidden">
        <div className="absolute inset-0 dotted-bg opacity-60" />
        <div className="absolute inset-x-0 top-0 h-72 glow pointer-events-none" />
        <div className="relative"><Logo /></div>
        <div className="relative max-w-md">
          <p className="text-xs font-semibold uppercase tracking-[0.14em] text-primary mb-4">{t("auth.panelEyebrow")}</p>
          <h2 className="text-[2rem] leading-[1.18] font-semibold tracking-tight text-foreground">
            {t("auth.panelVision")}
          </h2>
        </div>
        <div className="relative flex items-center gap-6 text-xs text-muted-foreground">
          <span className="inline-flex items-center gap-1.5"><ShieldCheck size={14} className="text-primary" /> {t("auth.panelVerified")}</span>
          <span className="inline-flex items-center gap-1.5"><Check size={14} className="text-success" /> {t("auth.panelApproved")}</span>
        </div>
      </div>

      {/* Form panel */}
      <div className="flex flex-col justify-center px-5 sm:px-10 py-12 bg-background relative">
        <div className="absolute top-5 right-5"><LanguageSwitcher variant="outline" /></div>
        <div className="w-full max-w-md mx-auto">
          <div className="lg:hidden mb-8"><Logo /></div>
          <h1 className="text-2xl font-semibold tracking-tight">{title}</h1>
          {subtitle && <p className="text-sm text-muted-foreground mt-1.5">{subtitle}</p>}
          <div className="mt-8">{children}</div>
        </div>
      </div>
    </div>
  );
}

export function Field({ label, children }) {
  return (
    <div className="space-y-1.5">
      <Label className="text-sm">{label}</Label>
      {children}
    </div>
  );
}

function GoogleButton() {
  const { t } = useTranslation();
  const go = () => {
    const redirect = `${window.location.origin}/`;
    window.location.href = `https://auth.emergentagent.com/?redirect=${encodeURIComponent(redirect)}`;
  };
  return (
    <Button type="button" variant="outline" onClick={go} data-testid="google-btn" className="w-full h-11 rounded-lg bg-card gap-2">
      <svg width="17" height="17" viewBox="0 0 24 24"><path fill="#4285F4" d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92a5.06 5.06 0 0 1-2.2 3.32v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.1Z"/><path fill="#34A853" d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84A11 11 0 0 0 12 23Z"/><path fill="#FBBC05" d="M5.84 14.1a6.6 6.6 0 0 1 0-4.2V7.06H2.18a11 11 0 0 0 0 9.88l3.66-2.84Z"/><path fill="#EA4335" d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1A11 11 0 0 0 2.18 7.06l3.66 2.84C6.71 7.31 9.14 5.38 12 5.38Z"/></svg>
      {t("auth.continueGoogle")}
    </Button>
  );
}

const TEST_ACCOUNTS = [
  { label: "Admin", email: "admin@compeni.com", password: "admin123" },
  { label: "HR", email: "hr@compeni.com", password: "hr123" },
  { label: "User", email: "employee@compeni.com", password: "employee123" },
  { label: "Expert", email: "expert@compeni.com", password: "expert123" },
];
const DEV = process.env.NODE_ENV !== "production";

export default function Login() {
  const { setUser } = useAuth();
  const navigate = useNavigate();
  const { t } = useTranslation();
  const [form, setForm] = useState({ email: "", password: "" });
  const [loading, setLoading] = useState(false);

  const runLogin = async (email, password) => {
    setLoading(true);
    try {
      const { data } = await api.post("/auth/login", { email, password });
      setUser(data.user);
      toast.success(t("auth.welcomeBack", { name: data.user.name.split(" ")[0] }));
      navigate(roleHome(data.user));
    } catch (err) {
      toast.error(apiError(err, t("auth.invalidCredentials")));
    } finally {
      setLoading(false);
    }
  };

  const submit = (e) => {
    e.preventDefault();
    runLogin(form.email, form.password);
  };

  return (
    <AuthLayout title={t("auth.loginTitle")} subtitle={t("auth.loginSubtitle")}>
      <form onSubmit={submit} className="space-y-4">
        <Field label={t("auth.workEmail")}>
          <Input data-testid="login-email" type="email" required autoComplete="email" placeholder="you@company.com"
            value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} className="h-11 rounded-lg" />
        </Field>
        <Field label={t("auth.password")}>
          <Input data-testid="login-password" type="password" required autoComplete="current-password" placeholder="••••••••"
            value={form.password} onChange={(e) => setForm({ ...form, password: e.target.value })} className="h-11 rounded-lg" />
        </Field>
        <div className="flex justify-end -mt-1">
          <Link to="/forgot-password" data-testid="forgot-password-link" className="text-xs font-medium text-primary hover:underline">
            {t("auth.forgot.link")}
          </Link>
        </div>
        <Button data-testid="login-submit" type="submit" disabled={loading} className="w-full h-11 rounded-lg active:scale-[0.98] transition-transform">
          {loading ? <Spinner /> : <>{t("auth.signInCta")} <ArrowRight size={16} className="ml-1" /></>}
        </Button>
      </form>
      <div className="my-5 flex items-center gap-3 text-xs text-muted-foreground">
        <span className="flex-1 h-px bg-border" /> {t("auth.or")} <span className="flex-1 h-px bg-border" />
      </div>
      <GoogleButton />
      {DEV && (
        <div data-testid="dev-credentials" className="mt-6 rounded-lg border border-dashed border-border bg-secondary/40 p-3">
          <p className="text-[11px] font-medium uppercase tracking-[0.08em] text-muted-foreground mb-2">{t("auth.testAccounts")}</p>
          <div className="grid grid-cols-2 gap-2">
            {TEST_ACCOUNTS.map((a) => (
              <button
                key={a.email}
                type="button"
                data-testid={`quick-${a.label.toLowerCase()}`}
                onClick={() => runLogin(a.email, a.password)}
                disabled={loading}
                className="text-left rounded-md border border-border bg-card px-3 py-2 hover:border-primary/40 hover:bg-card transition-colors disabled:opacity-60"
              >
                <span className="block text-xs font-medium text-foreground">{a.label}</span>
                <span className="block text-[11px] text-muted-foreground truncate">{a.email}</span>
              </button>
            ))}
          </div>
          <p className="text-[10px] text-muted-foreground mt-2">{t("auth.autoRemoved")}</p>
        </div>
      )}
      <p className="text-sm text-muted-foreground text-center mt-6">
        {t("auth.newToCompeni")} <Link to="/register/company" data-testid="to-register" className="text-primary font-medium hover:underline">{t("auth.createAccount")}</Link>
      </p>
    </AuthLayout>
  );
}

export function Register({ mode = "company" }) {
  const { setUser } = useAuth();
  const navigate = useNavigate();
  const { t } = useTranslation();
  const [params] = useSearchParams();
  const isExpert = mode === "expert";
  const inviteToken = isExpert ? params.get("invite") : null;
  const [invite, setInvite] = useState(null);
  const [loading, setLoading] = useState(false);
  const [consent, setConsent] = useState(false);
  const [marketing, setMarketing] = useState(false);
  const [terms, setTerms] = useState(false);
  const [c, setC] = useState({ company_name: "", industry: "Technology", size: "11-50", name: "", email: "", password: "" });
  const [x, setX] = useState({ name: "", email: "", password: "", title: "" });

  useEffect(() => {
    if (!inviteToken) return;
    api.get(`/auth/coach-invite/${inviteToken}`).then(({ data }) => {
      if (data?.status === "pending") {
        setInvite(data);
        setX((prev) => ({ ...prev, name: prev.name || data.name || "", email: prev.email || data.email || "" }));
      }
    }).catch(() => {});
  }, [inviteToken]);

  const finish = (user, msg) => { setUser(user); toast.success(msg); navigate(roleHome(user)); };
  const consentPayload = () => ({ privacy_consent: consent, marketing_consent: marketing, terms_consent: terms });

  const submitCompany = async (e) => {
    e.preventDefault(); setLoading(true);
    try { const { data } = await api.post("/auth/register/company", { ...c, ...consentPayload() }); finish(data.user, t("auth.workspaceCreated")); }
    catch (err) { toast.error(apiError(err, t("auth.couldNotRegister"))); } finally { setLoading(false); }
  };
  const submitExpert = async (e) => {
    e.preventDefault(); setLoading(true);
    try { const { data } = await api.post("/auth/register/expert", { ...x, ...consentPayload(), coach_invite_token: inviteToken || undefined }); finish(data.user, t("auth.applicationReceived")); }
    catch (err) { toast.error(apiError(err, t("auth.couldNotRegister"))); } finally { setLoading(false); }
  };

  const consentBlock = (
    <div className="space-y-2.5 pt-1">
      <label className="flex items-start gap-2.5 cursor-pointer">
        <Checkbox data-testid="consent-privacy" checked={consent} onCheckedChange={(v) => setConsent(v === true)} className="mt-0.5" />
        <span className="text-xs text-muted-foreground leading-relaxed">
          <Trans i18nKey="auth.consentPrivacy" components={{ lnk: <Link to="/privacy" target="_blank" className="text-primary hover:underline" /> }} /> <span className="text-destructive">*</span>
        </span>
      </label>
      <label className="flex items-start gap-2.5 cursor-pointer">
        <Checkbox data-testid="consent-terms" checked={terms} onCheckedChange={(v) => setTerms(v === true)} className="mt-0.5" />
        <span className="text-xs text-muted-foreground leading-relaxed">
          <Trans i18nKey="auth.consentTerms" components={{ lnk: <Link to="/terms" target="_blank" className="text-primary hover:underline" /> }} /> <span className="text-destructive">*</span>
        </span>
      </label>
      <label className="flex items-start gap-2.5 cursor-pointer">
        <Checkbox data-testid="consent-marketing" checked={marketing} onCheckedChange={(v) => setMarketing(v === true)} className="mt-0.5" />
        <span className="text-xs text-muted-foreground leading-relaxed">{t("auth.consentMarketing")}</span>
      </label>
    </div>
  );

  const title = isExpert ? t("auth.expertTitle") : t("auth.companyTitle");
  const subtitle = isExpert ? t("auth.expertSubtitle") : t("auth.companySubtitle");

  return (
    <AuthLayout title={title} subtitle={subtitle}>
      <div className="mb-6 inline-flex items-center gap-2 rounded-full border border-border bg-secondary/50 px-3 py-1.5 text-xs font-medium text-muted-foreground" data-testid={`register-badge-${mode}`}>
        {isExpert ? <ShieldCheck size={14} className="text-primary" /> : <Building2 size={14} className="text-primary" />}
        {isExpert ? t("auth.badgeExpert") : t("auth.badgeCompany")}
      </div>

      {isExpert && invite && (
        <div data-testid="coach-invite-banner" className="mb-6 rounded-lg border border-primary/30 bg-primary/5 p-4">
          <p className="text-sm font-medium text-foreground flex items-center gap-1.5">
            <Sparkles size={15} className="text-primary shrink-0" /> {t("auth.coachInviteTitle", { company: invite.company_name })}
          </p>
          <p className="text-xs text-muted-foreground mt-1.5 leading-relaxed">{t("auth.coachInviteBody")}</p>
          {invite.message && <p className="text-xs italic text-muted-foreground mt-2 border-l-2 border-primary/30 pl-2.5">“{invite.message}”</p>}
        </div>
      )}

      {isExpert ? (
        <form onSubmit={submitExpert} className="space-y-4" data-testid="expert-register-form">
          <Field label={t("auth.fullName")}>
            <Input data-testid="x-name" required placeholder="Alex Morgan" value={x.name} onChange={(e) => setX({ ...x, name: e.target.value })} className="h-11 rounded-lg" />
          </Field>
          <Field label={t("auth.proTitle")}>
            <Input data-testid="x-title" placeholder="Executive & Leadership Coach" value={x.title} onChange={(e) => setX({ ...x, title: e.target.value })} className="h-11 rounded-lg" />
          </Field>
          <Field label={t("auth.email")}>
            <Input data-testid="x-email" type="email" required placeholder="you@email.com" value={x.email} onChange={(e) => setX({ ...x, email: e.target.value })} className="h-11 rounded-lg" />
          </Field>
          <Field label={t("auth.password")}>
            <Input data-testid="x-password" type="password" required minLength={6} placeholder={t("auth.passwordHint")} value={x.password} onChange={(e) => setX({ ...x, password: e.target.value })} className="h-11 rounded-lg" />
          </Field>
          {consentBlock}
          <Button data-testid="x-submit" type="submit" disabled={loading || !consent || !terms} className="w-full h-11 rounded-lg active:scale-[0.98] transition-transform">
            {loading ? <Spinner /> : <>{t("auth.submitApplication")} <ArrowRight size={16} className="ml-1" /></>}
          </Button>
          <p className="text-xs text-muted-foreground">{t("auth.expertNote")}</p>
        </form>
      ) : (
        <form onSubmit={submitCompany} className="space-y-4" data-testid="company-register-form">
          <Field label={t("auth.companyName")}>
            <Input data-testid="c-company" required placeholder="Acme Inc." value={c.company_name} onChange={(e) => setC({ ...c, company_name: e.target.value })} className="h-11 rounded-lg" />
          </Field>
          <div className="grid grid-cols-2 gap-3">
            <Field label={t("auth.industry")}>
              <Select value={c.industry} onValueChange={(v) => setC({ ...c, industry: v })}>
                <SelectTrigger data-testid="c-industry" className="h-11 rounded-lg"><SelectValue /></SelectTrigger>
                <SelectContent>{INDUSTRIES.map((i) => <SelectItem key={i} value={i}>{i}</SelectItem>)}</SelectContent>
              </Select>
            </Field>
            <Field label={t("auth.teamSize")}>
              <Select value={c.size} onValueChange={(v) => setC({ ...c, size: v })}>
                <SelectTrigger data-testid="c-size" className="h-11 rounded-lg"><SelectValue /></SelectTrigger>
                <SelectContent>{COMPANY_SIZES.map((s) => <SelectItem key={s} value={s}>{t("auth.people", { size: s })}</SelectItem>)}</SelectContent>
              </Select>
            </Field>
          </div>
          <Field label={t("auth.yourName")}>
            <Input data-testid="c-name" required placeholder="Jane Doe" value={c.name} onChange={(e) => setC({ ...c, name: e.target.value })} className="h-11 rounded-lg" />
          </Field>
          <Field label={t("auth.workEmail")}>
            <Input data-testid="c-email" type="email" required placeholder="you@company.com" value={c.email} onChange={(e) => setC({ ...c, email: e.target.value })} className="h-11 rounded-lg" />
          </Field>
          <Field label={t("auth.password")}>
            <Input data-testid="c-password" type="password" required minLength={6} placeholder={t("auth.passwordHint")} value={c.password} onChange={(e) => setC({ ...c, password: e.target.value })} className="h-11 rounded-lg" />
          </Field>
          {consentBlock}
          <Button data-testid="c-submit" type="submit" disabled={loading || !consent || !terms} className="w-full h-11 rounded-lg active:scale-[0.98] transition-transform">
            {loading ? <Spinner /> : <>{t("auth.createWorkspace")} <ArrowRight size={16} className="ml-1" /></>}
          </Button>
          <p className="text-xs text-muted-foreground">{t("auth.employeesNote")}</p>
        </form>
      )}

      <div className="mt-6 rounded-lg border border-border bg-secondary/40 p-3.5 flex items-center justify-between gap-3">
        <span className="text-xs text-muted-foreground">
          {isExpert ? t("auth.crossCompanyQ") : t("auth.crossExpertQ")}
        </span>
        {isExpert ? (
          <Link to="/register/company" data-testid="switch-to-company" className="inline-flex items-center gap-1.5 text-xs font-medium text-primary hover:underline">
            <Building2 size={13} /> {t("auth.crossCompanyLink")}
          </Link>
        ) : (
          <Link to="/register/expert" data-testid="switch-to-expert" className="inline-flex items-center gap-1.5 text-xs font-medium text-primary hover:underline">
            <ShieldCheck size={13} /> {t("auth.crossExpertLink")}
          </Link>
        )}
      </div>

      <p className="text-sm text-muted-foreground text-center mt-6">
        {t("auth.haveAccount")} <Link to="/login" data-testid="to-login" className="text-primary font-medium hover:underline">{t("common.signIn")}</Link>
      </p>
    </AuthLayout>
  );
}
